import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { ArrowRight, Check, FileText, Target, Handshake } from "lucide-react";

const COMMITMENT_ITEMS = [
  "I commit to attending scheduled strategy meetings",
  "I commit to participating in consulting calls",
  "I commit to communicating openly when facing challenges",
  "I commit to logging into the dashboard weekly",
  "I commit to updating my accomplishments, goals, and tasks",
  "I commit to logging self-care activities and moments of clarity"
];

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    purpose: "",
    motivation: "",
    primary_business_goal: "",
    consulting_expectations: "",
    commitment_agreed: false
  });
  const [commitments, setCommitments] = useState(COMMITMENT_ITEMS.map(() => false));

  const allCommitmentsChecked = commitments.every(Boolean);

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const response = await authFetch(`${API}/intake`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          ...form,
          commitment_agreed: true
        })
      });
      
      if (response.ok) {
        await checkAuth();
        toast.success("Welcome aboard! Your journey begins now.");
        navigate("/dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit intake");
      }
    } catch (_error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canProceedStep1 = form.purpose && form.motivation;
  const canProceedStep2 = form.primary_business_goal && form.consulting_expectations;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-emerald-600 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center space-x-2 mb-4">
            <div className="w-8 h-8 bg-white/10 rounded-md flex items-center justify-center">
              <span className="text-white font-bold text-sm">MB</span>
            </div>
            <span className="font-semibold text-white">My Biz Consulting</span>
          </div>
          <h1 className="text-3xl font-semibold text-white mb-2">Welcome, {user?.name || "there"}!</h1>
          <p className="text-emerald-100">Let's set you up for success</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-neutral-200 py-4 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: "Your Purpose", icon: Target },
              { num: 2, label: "Business Goals", icon: FileText },
              { num: 3, label: "Commitment", icon: Handshake }
            ].map((s, index) => (
              <div key={s.num} className="flex items-center">
                <div className={`flex items-center ${index > 0 ? "flex-1" : ""}`}>
                  {index > 0 && (
                    <div className={`h-0.5 w-16 sm:w-24 mx-2 ${step >= s.num ? "bg-emerald-500" : "bg-neutral-200"}`}></div>
                  )}
                  <div 
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      step > s.num ? "bg-emerald-500 text-white" :
                      step === s.num ? "bg-emerald-600 text-white" : "bg-neutral-100 text-neutral-400"
                    }`}
                  >
                    {step > s.num ? <Check className="w-5 h-5" /> : <s.icon className="w-5 h-5" />}
                  </div>
                </div>
                <span className={`ml-2 text-sm font-medium hidden sm:block ${step >= s.num ? "text-neutral-900" : "text-neutral-400"}`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        {step === 1 && (
          <div className="space-y-8 animate-fade-in-up" data-testid="onboarding-step-1">
            <div>
              <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Tell us about your purpose</h2>
              <p className="text-neutral-600">Understanding your 'why' helps us serve you better</p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="purpose" className="text-neutral-700 text-base">
                  What is your purpose for seeking consulting services?
                </Label>
                <Textarea
                  id="purpose"
                  placeholder="Describe why you're here and what you hope to achieve..."
                  value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                  className="min-h-32 bg-white border-neutral-200 focus:border-emerald-500"
                  data-testid="onboarding-purpose-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="motivation" className="text-neutral-700 text-base">
                  What motivates you to grow your business?
                </Label>
                <Textarea
                  id="motivation"
                  placeholder="Share what drives you and keeps you going..."
                  value={form.motivation}
                  onChange={(e) => setForm({ ...form, motivation: e.target.value })}
                  className="min-h-32 bg-white border-neutral-200 focus:border-emerald-500"
                  data-testid="onboarding-motivation-input"
                />
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button 
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
                className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8"
                data-testid="onboarding-next-step-1"
              >
                Continue
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-8 animate-fade-in-up" data-testid="onboarding-step-2">
            <div>
              <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Define your business goals</h2>
              <p className="text-neutral-600">Clarity on goals helps us create a focused strategy</p>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="primary_goal" className="text-neutral-700 text-base">
                  What is your primary business goal?
                </Label>
                <Textarea
                  id="primary_goal"
                  placeholder="Describe the main outcome you want to achieve..."
                  value={form.primary_business_goal}
                  onChange={(e) => setForm({ ...form, primary_business_goal: e.target.value })}
                  className="min-h-32 bg-white border-neutral-200 focus:border-emerald-500"
                  data-testid="onboarding-goal-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expectations" className="text-neutral-700 text-base">
                  What are your expectations from our consulting services?
                </Label>
                <Textarea
                  id="expectations"
                  placeholder="Tell us what success looks like to you..."
                  value={form.consulting_expectations}
                  onChange={(e) => setForm({ ...form, consulting_expectations: e.target.value })}
                  className="min-h-32 bg-white border-neutral-200 focus:border-emerald-500"
                  data-testid="onboarding-expectations-input"
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button 
                variant="outline"
                onClick={() => setStep(1)}
                className="h-12 px-6 border-neutral-300"
              >
                Back
              </Button>
              <Button 
                onClick={() => setStep(3)}
                disabled={!canProceedStep2}
                className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8"
                data-testid="onboarding-next-step-2"
              >
                Continue
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-8 animate-fade-in-up" data-testid="onboarding-step-3">
            <div>
              <h2 className="text-2xl font-semibold text-neutral-900 mb-2">Client Commitment Agreement</h2>
              <p className="text-neutral-600">Your success requires mutual commitment. Please review and agree.</p>
            </div>

            <div className="bg-white rounded-xl border border-neutral-200 p-6 space-y-4">
              <h3 className="font-semibold text-neutral-900">I commit to:</h3>
              
              {COMMITMENT_ITEMS.map((item, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Checkbox
                    id={`commitment-${index}`}
                    checked={commitments[index]}
                    onCheckedChange={(checked) => {
                      const newCommitments = [...commitments];
                      newCommitments[index] = checked;
                      setCommitments(newCommitments);
                    }}
                    className="mt-1 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    data-testid={`commitment-checkbox-${index}`}
                  />
                  <Label htmlFor={`commitment-${index}`} className="text-neutral-700 cursor-pointer">
                    {item}
                  </Label>
                </div>
              ))}
            </div>

            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
              <h3 className="font-semibold text-emerald-900 mb-2">What we commit to you:</h3>
              <ul className="space-y-2 text-emerald-800">
                <li className="flex items-start space-x-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Strategic guidance, tools, and frameworks</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Feedback and recommendations based on your inputs</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Professionalism, confidentiality, and integrity</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>Support for accountability and clarity</span>
                </li>
              </ul>
            </div>

            <div className="flex justify-between pt-4">
              <Button 
                variant="outline"
                onClick={() => setStep(2)}
                className="h-12 px-6 border-neutral-300"
              >
                Back
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={!allCommitmentsChecked || loading}
                className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8"
                data-testid="onboarding-submit-btn"
              >
                {loading ? "Submitting..." : "Accept & Begin"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
